import { FC, ReactNode, useEffect, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { SearchContextProvider, useSearchContext } from "../../../../../../context/searchContext";
import useI18n from "../../../../../../hooks/useTranslations";
import { CustomError } from "../../../../../../http/errors";
import { useWallMaterialsApi } from "../../../../../../http/vertigrip/useWallMaterials";
import FlexContent from "../../../../../ui/FlexContent";
import Header, { HeaderPath } from "../../../../../ui/Header";
import Tabs, { Tab } from "../../../../../ui/Tabs";
import { VERTIGRIP } from "../../../AdminSystemSettings";
import WallMaterialDetails from "./WallMaterialDetails";
import WallMaterialFixings from "./fixings/Fixings";

const WallMaterial: FC = () => {
  const i18n = useI18n();
  const { systemId } = useParams<{ systemId: string }>();
  const { installationTypeId } = useParams<{ installationTypeId: string }>();
  const { wallMaterialId } = useParams<{ wallMaterialId: string }>();
  const wallMaterialApi = useWallMaterialsApi();
  const [searchParams] = useSearchParams();
  const selectedIndex = Number(searchParams.get("tab")) || 0;

  const aborter = useRef(new AbortController());
  const { error, data, isMutating, trigger } = wallMaterialApi.getAdminWallMaterial(aborter.current.signal);

  const tabs: Tab[] = [
    { title: i18n.translation.common.generalData, hasSubTabs: true },
    { title: i18n.translation.wallMaterialFixings.plural },
  ];

  const path = [
    { url: `/admin/systems`, display: i18n.translation.systems.all },
    { url: `/admin/systems/${systemId}`, display: VERTIGRIP },
    {
      url: `/admin/systems/${systemId}/settings?tab=2`,
      display: `${i18n.translation.common.settings}: ${i18n.translation.installationTypes.plural}`,
    },
    {
      url: `/admin/systems/${systemId}/installation_types/${installationTypeId}?tab=5`,
      display: i18n.translation.wallMaterials.plural,
    },
    { display: data?.name ?? "" },
  ];

  useEffect(() => {
    wallMaterialId && trigger(wallMaterialId);
  }, [wallMaterialId]);

  const contents: ReactNode[] = [
    <FlexContent padding direction="row">
      <WallMaterialDetails wallMaterial={!error ? data : undefined} disabled={isMutating} />
    </FlexContent>,
    <WallMaterialFixings active={selectedIndex === 1} />,
  ];

  return (
    <SearchContextProvider>
      <WallMaterialContent
        title={`${i18n.translation.wallMaterials.singular}: ${data?.name ?? ""}`}
        error={error}
        path={path}
        tabs={tabs}
        contents={contents}
      />
    </SearchContextProvider>
  );
};

type WallMaterialContentProps = {
  title: string;
  error?: CustomError;
  path: HeaderPath[];
  tabs: Tab[];
  contents: ReactNode[];
};

const WallMaterialContent: FC<WallMaterialContentProps> = ({ title, error, path, tabs, contents }) => {
  const { actions, title: tabTitle } = useSearchContext();

  return (
    <>
      <Header title={tabTitle ? `${title} · ${tabTitle}` : title} error={error} path={path} buttons={actions} />
      <Tabs urlParameter="tab" tabs={tabs} contents={contents} />
    </>
  );
};

export default WallMaterial;
